import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, collection, getDocs,
  addDoc, updateDoc, orderBy, query, serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import CanvasCoachView from '../../components/CanvasCoachView';
import AISummary from '../../components/AISummary';
import WorkPlansPage from '../shared/WorkPlansPage';

const STAGE_COLORS = {
  'Problem-Solution Fit':        '#e8522a',
  'Product-Market Fit':          '#f4a261',
  'Early Growth & Monetization': '#4a7c3f',
  'Scaling & Regional Expansion':'#2a9d8f',
};

const ROLE_LABELS = {
  team:        'Team',
  coach:       'Coach',
  beneficiary: 'Company',
  client:      'Client',
};

const EMPTY_SESSION = {
  sessionDate: '', durationMins: 60, keyPoints: '',
  decisions: '', nextSessionDate: '', actionItems: [],
};

const EMPTY_ACTION = { text: '', assignedTo: 'beneficiary', dueDate: '', status: 'open' };

export default function CoachCompanyPage() {
  const { companyId } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();

  const [company, setCompany]       = useState(null);
  const [sessions, setSessions]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(() => window.location.hash === '#log-session');
  const [form, setForm]             = useState(EMPTY_SESSION);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const compSnap = await getDoc(doc(db, 'companies', companyId));
        if (!compSnap.exists()) { navigate('/scaling/coach'); return; }
        setCompany(compSnap.data());
        const sSnap = await getDocs(
          query(collection(db, 'companies', companyId, 'coachingSessions'), orderBy('sessionDate', 'desc'))
        );
        setSessions(sSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [companyId, navigate]);

  const addActionItem  = () => setForm(f => ({ ...f, actionItems: [...f.actionItems, { ...EMPTY_ACTION }] }));
  const updateActionItem = (idx, field, value) => setForm(f => {
    const items = [...f.actionItems]; items[idx] = { ...items[idx], [field]: value }; return { ...f, actionItems: items };
  });
  const removeActionItem = (idx) => setForm(f => ({ ...f, actionItems: f.actionItems.filter((_, i) => i !== idx) }));

  const handleSubmit = async () => {
    if (!form.sessionDate) { setError('Please enter a session date.'); return; }
    if (!form.keyPoints.trim()) { setError('Please enter key discussion points.'); return; }
    setError(null); setSubmitting(true);
    try {
      const sessionData = {
        companyId, coachId: profile.uid, coachName: profile.displayName,
        sessionDate: new Date(form.sessionDate),
        durationMins: parseInt(form.durationMins) || 60,
        keyPoints: form.keyPoints.trim(), decisions: form.decisions.trim(),
        nextSessionDate: form.nextSessionDate ? new Date(form.nextSessionDate) : null,
        actionItems: form.actionItems.filter(a => a.text.trim()),
        status: 'completed', createdAt: new Date(), updatedAt: new Date(),
      };
      const ref = await addDoc(collection(db, 'companies', companyId, 'coachingSessions'), sessionData);
      setSessions(prev => [{ id: ref.id, ...sessionData }, ...prev]);
      setForm(EMPTY_SESSION); setShowForm(false); setExpandedSession(ref.id);
    } catch (err) { console.error(err); setError('Failed to save session. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const toggleActionItem = async (sessionId, actionIdx, currentStatus) => {
    const newStatus = currentStatus === 'open' ? 'completed' : 'open';
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;
    const updatedItems = session.actionItems.map((a, i) => i === actionIdx ? { ...a, status: newStatus } : a);
    try {
      await updateDoc(doc(db, 'companies', companyId, 'coachingSessions', sessionId), { actionItems: updatedItems, updatedAt: new Date() });
      setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, actionItems: updatedItems } : s));
    } catch (err) { console.error(err); }
  };

  if (loading) return <DashboardLayout><div className="gm-loading"><div className="gm-spinner" /></div></DashboardLayout>;
  if (!company) return <DashboardLayout><div className="gm-page"><p>Company not found.</p></div></DashboardLayout>;

  const stageColor = STAGE_COLORS[company.currentStage] || '#4a5481';
  const openActions = sessions.flatMap(s =>
    (s.actionItems || []).filter(a => a.status === 'open').map(a => ({ ...a, sessionId: s.id }))
  );

  return (
    <DashboardLayout>
      <div className="gm-page">
        <div className="gm-page-header">
          <div>
            <button className="gm-back-btn" onClick={() => navigate('/scaling/coach')}>← My companies</button>
            <h1 className="gm-page-title" style={{ marginTop: 8 }}>{company.name}</h1>
            <p className="gm-page-sub">{company.description}</p>
          </div>
          <span className="gm-stage-pill" style={{ background: stageColor + '22', color: stageColor }}>{company.currentStage}</span>
        </div>

        {/* Stage comparison */}
        <div className="gm-stage-compare" style={{ marginBottom: 24 }}>
          <div className="gm-stage-compare-card">
            <div className="gm-stage-compare-label">Baseline (Immersion week)</div>
            <div className="gm-stage-compare-stage" style={{ color: STAGE_COLORS[company.currentStage] }}>{company.currentStage}</div>
            <div className="gm-stage-compare-upgrade">{company.currentStageUpgrade}</div>
            <div className="gm-mini-progress" style={{ marginTop: 8 }}>
              <div className="gm-mini-progress-fill" style={{ width: `${company.currentStageProgress||0}%`, background: STAGE_COLORS[company.currentStage] }} />
            </div>
            <div className="gm-mini-progress-label">{company.currentStageProgress}% through stage</div>
          </div>
          {company.latestStage && company.latestStage !== company.currentStage && (
            <div className="gm-stage-compare-card gm-stage-compare-card--current">
              <div className="gm-stage-compare-label">Latest assessment</div>
              <div className="gm-stage-compare-stage" style={{ color: STAGE_COLORS[company.latestStage] }}>{company.latestStage}</div>
              <div className="gm-stage-compare-upgrade">{company.latestStageUpgrade}</div>
              <div className="gm-mini-progress" style={{ marginTop: 8 }}>
                <div className="gm-mini-progress-fill" style={{ width: `${company.latestStageProgress||0}%`, background: STAGE_COLORS[company.latestStage] }} />
              </div>
              <div className="gm-mini-progress-label">{company.latestStageProgress}% through stage</div>
            </div>
          )}
          <div className="gm-stage-compare-card gm-stage-compare-card--target">
            <div className="gm-stage-compare-label">Target (Dec 2026)</div>
            <div className="gm-stage-compare-stage" style={{ color: STAGE_COLORS[company.targetStage] }}>{company.targetStage}</div>
            <div className="gm-stage-compare-upgrade">{company.targetStageUpgrade}</div>
            <div className="gm-mini-progress" style={{ marginTop: 8 }}>
              <div className="gm-mini-progress-fill" style={{ width: `${company.targetStageProgress||0}%`, background: STAGE_COLORS[company.targetStage] }} />
            </div>
            <div className="gm-mini-progress-label">{company.targetStageProgress}% through stage</div>
          </div>
          <div className="gm-kpi-card">
            <div className="gm-kpi-label">Sessions logged</div>
            <div className="gm-kpi-value">{sessions.length}</div>
          </div>
          <div className="gm-kpi-card">
            <div className="gm-kpi-label">Open actions</div>
            <div className="gm-kpi-value" style={{ color: openActions.length > 0 ? '#e8522a' : '#4a7c3f' }}>{openActions.length}</div>
          </div>
        </div>

        {/* Assessment journey */}
        <AssessmentJourney companyId={companyId} company={company} coachId={profile.uid} />

        {/* AI Summary */}
        <div className="gm-section">
          <h2 className="gm-section-title">Progress summary</h2>
          <AISummary companyId={companyId} companyName={company.name} role="coach" />
        </div>

        {/* Work Plans */}
        <div className="gm-section">
          <h2 className="gm-section-title">Work plans by bucket</h2>
          <WorkPlansPage companyId={companyId} isCoach={true} />
        </div>

        {/* Canvas & Journey Maps */}
        <CanvasCoachView companyId={companyId} profile={profile} />

        {/* Open actions summary */}
        {openActions.length > 0 && (
          <div className="gm-section">
            <h2 className="gm-section-title">Open action items</h2>
            <div className="gm-action-items-list">
              {openActions.map((a, idx) => (
                <div key={idx} className="gm-action-item gm-action-item--open">
                  <div className="gm-action-item-left">
                    <span className={`gm-action-assignee gm-action-assignee--${a.assignedTo}`}>
                      {a.assignedTo === 'coach' ? '👤 Coach' : '🏢 Company'}
                    </span>
                    <span className="gm-action-item-text">{a.text}</span>
                  </div>
                  {a.dueDate && <span className="gm-action-due">Due {formatDate(a.dueDate)}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sessions */}
        <div className="gm-section">
          <div className="gm-section-header">
            <h2 className="gm-section-title" style={{ marginBottom: 0 }}>Coaching sessions</h2>
            {!showForm && (
              <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={() => setShowForm(true)}>+ Log session</button>
            )}
          </div>

          {showForm && (
            <div className="gm-session-form">
              <h3 className="gm-session-form-title">New session</h3>
              {error && <div className="gm-alert gm-alert-error">{error}</div>}
              <div className="gm-form-row">
                <div className="gm-form-group">
                  <label>Session date *</label>
                  <input type="date" value={form.sessionDate} onChange={e => setForm(f => ({ ...f, sessionDate: e.target.value }))} />
                </div>
                <div className="gm-form-group">
                  <label>Duration (minutes)</label>
                  <input type="number" value={form.durationMins} min="15" step="15" onChange={e => setForm(f => ({ ...f, durationMins: e.target.value }))} />
                </div>
                <div className="gm-form-group">
                  <label>Next session date</label>
                  <input type="date" value={form.nextSessionDate} onChange={e => setForm(f => ({ ...f, nextSessionDate: e.target.value }))} />
                </div>
              </div>
              <div className="gm-form-group">
                <label>Key discussion points *</label>
                <textarea rows={4} placeholder="What was discussed in this session?" value={form.keyPoints} onChange={e => setForm(f => ({ ...f, keyPoints: e.target.value }))} />
              </div>
              <div className="gm-form-group">
                <label>Decisions made</label>
                <textarea rows={3} placeholder="What was decided or agreed?" value={form.decisions} onChange={e => setForm(f => ({ ...f, decisions: e.target.value }))} />
              </div>
              <div className="gm-form-group">
                <label>Action items</label>
                {form.actionItems.map((item, idx) => (
                  <div key={idx} className="gm-action-item-form">
                    <input type="text" placeholder="Action item description" value={item.text} onChange={e => updateActionItem(idx, 'text', e.target.value)} style={{ flex: 2 }} />
                    <select value={item.assignedTo} onChange={e => updateActionItem(idx, 'assignedTo', e.target.value)}>
                      <option value="beneficiary">Company</option>
                      <option value="coach">Coach</option>
                    </select>
                    <input type="date" value={item.dueDate} onChange={e => updateActionItem(idx, 'dueDate', e.target.value)} />
                    <button className="gm-btn-icon" onClick={() => removeActionItem(idx)} title="Remove">✕</button>
                  </div>
                ))}
                <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={addActionItem} style={{ marginTop: 8 }}>+ Add action item</button>
              </div>
              <div className="gm-session-form-actions">
                <button className="gm-btn gm-btn-primary" onClick={handleSubmit} disabled={submitting}>{submitting ? 'Saving…' : 'Save session'}</button>
                <button className="gm-btn gm-btn-outline" onClick={() => { setShowForm(false); setError(null); setForm(EMPTY_SESSION); }}>Cancel</button>
              </div>
            </div>
          )}

          {sessions.length === 0 && !showForm && (
            <div className="gm-empty-state">
              <p>No sessions logged yet.</p>
              <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={() => setShowForm(true)}>Log first session</button>
            </div>
          )}

          {sessions.map(session => (
            <SessionCard
              key={session.id}
              session={session}
              companyId={companyId}
              isExpanded={expandedSession === session.id}
              onToggle={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
              onToggleAction={(actionIdx, status) => toggleActionItem(session.id, actionIdx, status)}
              isCoach={true}
              profile={profile}
            />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ─── Session Card with comments ───────────────────────────────
export function SessionCard({ session, companyId, isExpanded, onToggle, onToggleAction, isCoach, profile, readOnly }) {
  const [comments, setComments]       = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [posting, setPosting]         = useState(false);
  const [actionComments, setActionComments] = useState({});
  const [actionCommentText, setActionCommentText] = useState({});
  const commentEndRef = useRef(null);

  const date = session.sessionDate?.toDate
    ? session.sessionDate.toDate()
    : session.sessionDate instanceof Date ? session.sessionDate : new Date(session.sessionDate);

  const openCount = (session.actionItems || []).filter(a => a.status === 'open').length;

  // Load comments when expanded
  useEffect(() => {
    if (!isExpanded || commentsLoaded || !companyId) return;
    async function loadComments() {
      try {
        const snap = await getDocs(
          query(collection(db, 'companies', companyId, 'coachingSessions', session.id, 'comments'),
            orderBy('createdAt', 'asc'))
        );
        setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setCommentsLoaded(true);
      } catch (err) { console.error(err); }
    }
    loadComments();
  }, [isExpanded, commentsLoaded, companyId, session.id]);

  const postComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || posting) return;
    setPosting(true);
    try {
      const commentData = {
        body:        commentText.trim(),
        authorUid:   profile.uid,
        authorName:  profile.displayName,
        authorRole:  profile.role,
        createdAt:   serverTimestamp(),
      };
      const ref = await addDoc(
        collection(db, 'companies', companyId, 'coachingSessions', session.id, 'comments'),
        commentData
      );
      setComments(prev => [...prev, { id: ref.id, ...commentData, createdAt: new Date() }]);
      setCommentText('');
      setTimeout(() => commentEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { console.error(err); }
    finally { setPosting(false); }
  };

  const postActionComment = async (actionIdx) => {
    const text = actionCommentText[actionIdx]?.trim();
    if (!text) return;
    const key = `action_${actionIdx}`;
    const commentData = {
      body:       text,
      authorUid:  profile.uid,
      authorName: profile.displayName,
      authorRole: profile.role,
      actionIdx,
      createdAt:  new Date(),
    };
    try {
      const ref = await addDoc(
        collection(db, 'companies', companyId, 'coachingSessions', session.id, 'comments'),
        commentData
      );
      setActionComments(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), { id: ref.id, ...commentData }]
      }));
      setActionCommentText(prev => ({ ...prev, [actionIdx]: '' }));
    } catch (err) { console.error(err); }
  };

  // Session-level comments (no actionIdx)
  const sessionComments = comments.filter(c => c.actionIdx == null);
  // Action-level comments
  const getActionComments = (idx) => [
    ...comments.filter(c => c.actionIdx === idx),
    ...(actionComments[`action_${idx}`] || [])
  ];

  return (
    <div className="gm-session-card">
      <div className="gm-session-card-header" onClick={onToggle}>
        <div className="gm-session-card-left">
          <div className="gm-session-date">{formatDate(date)}</div>
          <div className="gm-session-meta">
            {session.durationMins && <span>{session.durationMins} mins</span>}
            {session.actionItems?.length > 0 && (
              <span className={openCount > 0 ? 'gm-session-open-count' : ''}>
                {openCount > 0 ? `${openCount} open action${openCount > 1 ? 's' : ''}` : 'All actions done ✓'}
              </span>
            )}
            {sessionComments.length > 0 && <span>💬 {sessionComments.length} comment{sessionComments.length > 1 ? 's' : ''}</span>}
          </div>
          <p className="gm-session-preview">{session.keyPoints?.slice(0, 100)}{session.keyPoints?.length > 100 ? '…' : ''}</p>
        </div>
        <span className="gm-session-toggle">{isExpanded ? '▲' : '▼'}</span>
      </div>

      {isExpanded && (
        <div className="gm-session-card-body">
          <div className="gm-session-section">
            <div className="gm-session-section-label">Key discussion points</div>
            <p className="gm-session-section-text">{session.keyPoints}</p>
          </div>

          {session.decisions && (
            <div className="gm-session-section">
              <div className="gm-session-section-label">Decisions made</div>
              <p className="gm-session-section-text">{session.decisions}</p>
            </div>
          )}

          {session.actionItems?.length > 0 && (
            <div className="gm-session-section">
              <div className="gm-session-section-label">Action items</div>
              {session.actionItems.map((a, idx) => (
                <div key={idx} className="gm-action-item-wrap">
                  <div className={`gm-action-item ${a.status === 'completed' ? 'gm-action-item--done' : 'gm-action-item--open'}`}>
                    <div className="gm-action-item-left">
                      {isCoach && !readOnly ? (
                        <button
                          className={`gm-action-check ${a.status === 'completed' ? 'gm-action-check--done' : ''}`}
                          onClick={() => onToggleAction(idx, a.status)}
                        >
                          {a.status === 'completed' ? '✓' : '○'}
                        </button>
                      ) : (
                        <span className={`gm-action-check ${a.status === 'completed' ? 'gm-action-check--done' : ''}`}>
                          {a.status === 'completed' ? '✓' : '○'}
                        </span>
                      )}
                      <span className={`gm-action-assignee gm-action-assignee--${a.assignedTo}`}>
                        {a.assignedTo === 'coach' ? '👤 Coach' : '🏢 Company'}
                      </span>
                      <span className={`gm-action-item-text ${a.status === 'completed' ? 'gm-action-item-text--done' : ''}`}>{a.text}</span>
                    </div>
                    {a.dueDate && <span className="gm-action-due">Due {formatDate(a.dueDate)}</span>}
                  </div>

                  {/* Action item comments */}
                  <div className="gm-action-comments">
                    {getActionComments(idx).map((c, ci) => (
                      <CommentBubble key={c.id || ci} comment={c} isOwn={c.authorUid === profile?.uid} />
                    ))}
                    {!readOnly && (
                      <div className="gm-comment-input-row">
                        <input
                          type="text"
                          placeholder="Add a note on this action…"
                          value={actionCommentText[idx] || ''}
                          onChange={e => setActionCommentText(prev => ({ ...prev, [idx]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); postActionComment(idx); }}}
                        />
                        <button className="gm-btn gm-btn-sm gm-btn-outline"
                          onClick={() => postActionComment(idx)}
                          disabled={!actionCommentText[idx]?.trim()}>
                          Post
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Session comments thread */}
          <div className="gm-session-section">
            <div className="gm-session-section-label">
              Comments {sessionComments.length > 0 && `· ${sessionComments.length}`}
            </div>
            <div className="gm-comments-thread">
              {sessionComments.length === 0 && (
                <p className="gm-comments-empty">No comments yet. Be the first to add one.</p>
              )}
              {sessionComments.map((c, idx) => (
                <CommentBubble key={c.id || idx} comment={c} isOwn={c.authorUid === profile?.uid} />
              ))}
              <div ref={commentEndRef} />
            </div>
            {!readOnly && (
              <form className="gm-comment-form" onSubmit={postComment}>
                <textarea
                  placeholder="Add a comment…"
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postComment(e); }}}
                  rows={2}
                />
                <button className="gm-btn gm-btn-primary gm-btn-sm" type="submit" disabled={posting || !commentText.trim()}>
                  {posting ? 'Posting…' : 'Post'}
                </button>
              </form>
            )}
          </div>

          {session.nextSessionDate && (
            <div className="gm-session-next">
              📅 Next session: {formatDate(
                session.nextSessionDate?.toDate ? session.nextSessionDate.toDate() : new Date(session.nextSessionDate)
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Comment bubble ───────────────────────────────────────────
function CommentBubble({ comment, isOwn }) {
  const date = comment.createdAt?.toDate
    ? comment.createdAt.toDate()
    : comment.createdAt instanceof Date ? comment.createdAt : null;

  return (
    <div className={`gm-comment ${isOwn ? 'gm-comment--own' : ''}`}>
      <div className="gm-comment-meta">
        <span className="gm-comment-author">{comment.authorName}</span>
        <span className={`gm-comment-role gm-action-assignee--${comment.authorRole}`}>
          {ROLE_LABELS[comment.authorRole] || comment.authorRole}
        </span>
        {date && <span className="gm-comment-time">{formatDate(date)}</span>}
      </div>
      <div className="gm-comment-body">{comment.body}</div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────
export function formatDate(date) {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Assessment Journey ───────────────────────────────────────
const ASSESSMENT_TYPE_ORDER = { baseline: 0, periodic: 1, target: 2 };

function AssessmentJourney({ companyId, company, coachId }) {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [validating, setValidating]   = useState(null);
  const [coachNotes, setCoachNotes]   = useState({});
  const [expanded, setExpanded]       = useState(null);
  const [savedNote, setSavedNote]       = useState(null); // id of last saved

  const STAGE_COLORS = {
    'Problem-Solution Fit':        '#e8522a',
    'Product-Market Fit':          '#f4a261',
    'Early Growth & Monetization': '#4a7c3f',
    'Scaling & Regional Expansion':'#2a9d8f',
  };

  const TYPE_LABELS = { baseline: 'Baseline', periodic: 'Periodic', target: 'Target' };

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(collection(db, 'companies', companyId, 'assessments'));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            if (a.type !== b.type) return ASSESSMENT_TYPE_ORDER[a.type] - ASSESSMENT_TYPE_ORDER[b.type];
            const da = a.assessmentDate?.toDate ? a.assessmentDate.toDate() : new Date(a.assessmentDate);
            const db2 = b.assessmentDate?.toDate ? b.assessmentDate.toDate() : new Date(b.assessmentDate);
            return da - db2;
          });
        setAssessments(all);
        // Auto-expand first pending
        const pending = all.find(a => a.status === 'submitted');
        if (pending) setExpanded(pending.id);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [companyId]);

  const handleSaveNotes = async (assessment) => {
    const newNote = coachNotes[assessment.id]?.trim();
    if (!newNote) return;
    setValidating(assessment.id);
    try {
      // Append new note with timestamp to existing notes
      const existing = assessment.coachNotes || '';
      const dateStr  = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const appended = existing
        ? `${existing}\n\n[${dateStr}] ${newNote}`
        : `[${dateStr}] ${newNote}`;
      await updateDoc(
        doc(db, 'companies', companyId, 'assessments', assessment.id),
        { coachNotes: appended, updatedAt: new Date(), coachNotesUpdatedAt: new Date() }
      );
      setAssessments(prev => prev.map(a =>
        a.id === assessment.id ? { ...a, coachNotes: appended } : a
      ));
      // Clear the input after saving
      setCoachNotes(prev => ({ ...prev, [assessment.id]: '' }));
      setSavedNote(assessment.id);
      setTimeout(() => setSavedNote(null), 3000);
    } catch (err) { console.error(err); }
    finally { setValidating(null); }
  };

  const handleValidate = async (assessment) => {
    setValidating(assessment.id);
    try {
      const notes = coachNotes[assessment.id] !== undefined
        ? coachNotes[assessment.id]
        : (assessment.coachNotes || '');
      await updateDoc(
        doc(db, 'companies', companyId, 'assessments', assessment.id),
        { status: 'validated', coachNotes: notes, validatedAt: new Date(), validatedBy: coachId }
      );
      setAssessments(prev => prev.map(a =>
        a.id === assessment.id ? { ...a, status: 'validated', coachNotes: notes } : a
      ));
    } catch (err) { console.error(err); }
    finally { setValidating(null); }
  };

  const QUESTIONS = [
    { id:'q01', label:'Problem validation', text:'Have you validated a real problem exists that your solution addresses?',
      options:[{score:0,label:'No validation yet'},{score:2,label:'Informal validation through discussions'},{score:5,label:'User surveys, interviews or market research'},{score:10,label:'Users are actively seeking solutions like yours'}]},
    { id:'q02', label:'MVP status', text:'What is the current status of your Minimum Viable Product (MVP)?',
      options:[{score:0,label:'No MVP developed yet'},{score:2,label:'MVP in early development'},{score:5,label:'MVP launched with early user feedback'},{score:10,label:'MVP iterated based on significant user engagement'}]},
    { id:'q03', label:'Active users/early adopters', text:'How many active users or early adopters does your solution have?',
      options:[{score:0,label:'Less than 50 users'},{score:2,label:'50–200 users'},{score:5,label:'200–500 users'},{score:10,label:'500+ users'}]},
    { id:'q04', label:'Funding secured', text:'Have you secured any early funding?',
      options:[{score:0,label:'No funding yet'},{score:2,label:'Small grants or personal funds'},{score:5,label:'Initial investment or accelerator'},{score:10,label:'Multiple funding sources'}]},
    { id:'q05', label:'Product resonance', text:'How well is your product resonating with users?',
      options:[{score:0,label:'Low engagement or inconsistent usage'},{score:3,label:'Moderate engagement but no repeat usage'},{score:7,label:'High engagement with repeat usage'},{score:10,label:'Strong demand with organic referrals'}]},
    { id:'q06', label:'User retention', text:'What percentage of users return to use your solution?',
      options:[{score:0,label:'Less than 20% retention'},{score:3,label:'20–50% retention'},{score:7,label:'50–70% retention'},{score:10,label:'Above 70% retention'}]},
    { id:'q07', label:'Revenue traction', text:'What revenue traction do you currently have?',
      options:[{score:0,label:'No revenue yet'},{score:3,label:'Some one-time sales, small transactions'},{score:7,label:'Generating monthly revenue between ETB2k–ETB10k'},{score:10,label:'Consistent revenue over ETB10k per month'}]},
    { id:'q08', label:'B2B partnerships', text:'Have you established any B2B or institutional partnerships?',
      options:[{score:0,label:'No partnerships'},{score:3,label:'Initial discussions with potential partners'},{score:7,label:'One or two small partnerships'},{score:10,label:'Multiple high level partnerships'}]},
    { id:'q09', label:'Total active users', text:'How many active users do you currently serve?',
      options:[{score:0,label:'Less than 1k users'},{score:3,label:'1k–10k users'},{score:7,label:'10k–50k users'},{score:10,label:'50k+ users'}]},
    { id:'q10', label:'Regional penetration', text:'What is your regional market penetration?',
      options:[{score:0,label:'Limited to one locality or pilot users'},{score:3,label:'Expanded to multiple locations but not scaling'},{score:7,label:'Operating in 2–5 regions'},{score:10,label:'Operating in 5+ regions'}]},
    { id:'q11', label:'Monthly revenue range', text:'What is your revenue range per month?',
      options:[{score:0,label:'Less than ETB10k'},{score:3,label:'ETB10k–ETB50k'},{score:7,label:'ETB50k–ETB200k'},{score:10,label:'ETB200k+'}]},
    { id:'q12', label:'Customer acquisition', text:'How robust is your customer acquisition strategy?',
      options:[{score:0,label:'No structured marketing or sales strategy'},{score:3,label:'Some organic growth through word-of-mouth'},{score:7,label:'Paid marketing with measurable results'},{score:10,label:'Scalable acquisition strategy driving consistent growth'}]},
    { id:'q13', label:'Total user base', text:'What is your total user base?',
      options:[{score:0,label:'Less than 10k users'},{score:5,label:'10k–100k users'},{score:7,label:'100k–500k users'},{score:10,label:'500k+ users'}]},
    { id:'q14', label:'Market expansion', text:'Have you expanded outside your initial market?',
      options:[{score:0,label:'Still in initial market'},{score:3,label:'Exploring opportunities for expansion'},{score:7,label:'Expanded to at least one new country'},{score:10,label:'Operating in multiple countries'}]},
    { id:'q15', label:'Annual revenue', text:'What is your annual revenue?',
      options:[{score:0,label:'Less than ETB500k'},{score:5,label:'ETB500k–ETB1M'},{score:7,label:'ETB1M–ETB3M'},{score:10,label:'Above ETB3M'}]},
    { id:'q16', label:'Gov. integration', text:'What level of institutional/government integration do you have?',
      options:[{score:0,label:'No government or institutional support'},{score:3,label:'Engaged in discussions'},{score:7,label:'Government adoption or funding secured'},{score:10,label:'Fully integrated into government policies or national programs'}]},
  ];

  if (loading) return <div className="gm-loading"><div className="gm-spinner" /></div>;

  return (
    <div className="gm-section">
      <h2 className="gm-section-title">Assessment journey</h2>

      {assessments.length === 0 && (
        <div className="gm-empty-state"><p>No assessments recorded yet.</p></div>
      )}

      <div className="gm-assessment-timeline">
        {assessments.map((a, idx) => {
          const date = a.assessmentDate?.toDate
            ? a.assessmentDate.toDate()
            : new Date(a.assessmentDate);
          const isPending   = a.status === 'submitted';
          const isValidated = a.status === 'validated';
          const isExpanded  = expanded === a.id;
          const color       = STAGE_COLORS[a.stage] || '#4a5481';

          return (
            <div key={a.id} className={`gm-assessment-entry ${isPending ? 'gm-assessment-entry--pending' : ''}`}>
              {/* Header row */}
              <div className="gm-assessment-entry-header" onClick={() => setExpanded(isExpanded ? null : a.id)}>
                <div className="gm-assessment-entry-left">
                  <span className={`gm-assessment-type-badge gm-assessment-type-badge--${a.type}`}>
                    {TYPE_LABELS[a.type]}
                  </span>
                  {isPending && <span className="gm-pending-badge">🔔 Awaiting validation</span>}
                  {isValidated && <span className="gm-validated-badge">✓ Validated</span>}
                  <div className="gm-assessment-entry-stage" style={{ color }}>{a.stage}</div>
                  <div className="gm-assessment-entry-meta">
                    {formatDate(date)} · {a.stageUpgrade} · {a.stageProgress}% through stage
                  </div>
                </div>
                <div className="gm-assessment-entry-right">
                  <div className="gm-mini-progress" style={{ width: 120 }}>
                    <div className="gm-mini-progress-fill" style={{ width: `${a.stageProgress||0}%`, background: color }} />
                  </div>
                  <span className="gm-session-toggle">{isExpanded ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="gm-assessment-entry-body">
                  {/* Question breakdown */}
                  {a.questionScores && (
                    <div className="gm-assessment-q-full">
                      {QUESTIONS.map((q) => {
                        const score = a.questionScores[q.id];
                        const answerLabel = q.options.find(o => o.score === score)?.label || '—';
                        const isNull = score == null;
                        return (
                          <div key={q.id} className="gm-assessment-q-detail">
                            <div className="gm-assessment-q-detail-header">
                              <span className="gm-question-number">{q.id.toUpperCase()}</span>
                              <span className="gm-assessment-q-detail-text">{q.text}</span>
                            </div>
                            <div className="gm-assessment-q-detail-answer">
                              <div className="gm-assessment-q-bar" style={{ flex: 1 }}>
                                <div className="gm-assessment-q-fill"
                                  style={{ width: `${isNull ? 0 : ((score)/10)*100}%`, background: color }} />
                              </div>
                              <span className="gm-assessment-q-answer-label"
                                style={{ color: isNull ? '#b0b0b0' : color }}>
                                {answerLabel}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Coach notes */}
                  {a.coachNotes && (
                    <div className="gm-coach-notes" style={{ marginTop: 16 }}>
                      <h3>Coach notes</h3>
                      <p>{a.coachNotes}</p>
                    </div>
                  )}

                  {/* Coach notes — available any time, not tied to validation */}
                  <div className="gm-validation-action">
                    {/* Show existing notes as read-only log */}
                    {a.coachNotes && (
                      <div className="gm-coach-notes" style={{ marginBottom: 12 }}>
                        <h3>Coach notes log</h3>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{a.coachNotes}</p>
                      </div>
                    )}
                    <div className="gm-form-group">
                      <label>Add a note</label>
                      <textarea
                        rows={3}
                        placeholder="Add a new observation or feedback (will be appended to existing notes)…"
                        value={coachNotes[a.id] || ''}
                        onChange={e => setCoachNotes(prev => ({ ...prev, [a.id]: e.target.value }))}
                      />
                    </div>
                    <div className="gm-validation-action-btns">
                      <button
                        className="gm-btn gm-btn-outline gm-btn-sm"
                        onClick={() => handleSaveNotes(a)}
                        disabled={validating === a.id}
                      >
                        {validating === a.id ? 'Saving…' : 'Save notes'}
                      </button>
                      {savedNote === a.id && (
                        <span className="gm-save-success">✓ Notes saved</span>
                      )}
                      {isPending && (
                        <button
                          className="gm-btn gm-btn-primary gm-btn-sm"
                          onClick={() => handleValidate(a)}
                          disabled={validating === a.id}
                        >
                          {validating === a.id ? 'Validating…' : '✓ Validate assessment'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
